import {
    noDateGroupLabel,
    laterGroupLabel,
    nextWeekGroupLabel,
    thisWeekGroupLabel,
    tomorrowGroupLabel,
    todayGroupLabel,
    unflaggedDueDateGroupLabel,
} from './getGroupHeaderForFlagDueDate.locstring.json';
import loc from 'owa-localize';
import type GroupHeader from '../type/GroupHeader';
import { FlagDueDateHeaderId } from '../type/GroupHeaderId';
import type { TableView } from 'owa-mail-list-store';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import { userDate, addDays, startOfDay, startOfWeek } from 'owa-datetime';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

/**
 * Get GroupHeader for row when sorted by FlagDueDate.
 * Buckets a row into one of seven groups: Unflagged, NoDate, Today, Tomorrow, ThisWeek, NextWeek, Later.
 * - Unflagged: rows with no flag set or flag status 'Complete'
 * - NoDate: rows that are flagged but have no due date set
 * - Today/Tomorrow/ThisWeek/NextWeek/Later: flagged rows bucketed by due date
 * Week boundaries are computed using the user's configured first day of week.
 * @param rowKey rowKey for which we want to lookup the flag due date group
 * @param tableView tableView
 * @return group header for this row
 */
export default function getGroupHeaderForFlagDueDate(
    rowKey: string,
    tableView: TableView
): GroupHeader {
    const flagStatus = MailRowDataPropertyGetter.getFlagStatus(rowKey, tableView);

    // Rows that are not flagged go to the Unflagged group
    if (!flagStatus || flagStatus === 'NotFlagged') {
        return {
            headerText: () => loc(unflaggedDueDateGroupLabel),
            headerId: FlagDueDateHeaderId.Unflagged,
        };
    }

    // Row is flagged or completed — check if it has a due date
    const flagDueDate = MailRowDataPropertyGetter.getFlagDueDate(rowKey, tableView);
    if (!flagDueDate || flagStatus === 'Complete') {
        return {
            headerText: () => loc(noDateGroupLabel),
            headerId: FlagDueDateHeaderId.NoDate,
        };
    }

    const todayStart = startOfDay(userDate());
    const due = startOfDay(userDate(flagDueDate)).valueOf();
    const weekStartDay = getUserConfiguration().UserOptions?.WeekStartDay ?? 0;
    const nextWeekStart = addDays(startOfWeek(todayStart, weekStartDay), 7);
    const weekAfterNextStart = addDays(nextWeekStart, 7);

    if (due < addDays(todayStart, 1).valueOf()) {
        return {
            headerText: () => loc(todayGroupLabel),
            headerId: FlagDueDateHeaderId.Today,
        };
    }
    if (due < addDays(todayStart, 2).valueOf()) {
        return {
            headerText: () => loc(tomorrowGroupLabel),
            headerId: FlagDueDateHeaderId.Tomorrow,
        };
    }
    if (due < nextWeekStart.valueOf()) {
        return {
            headerText: () => loc(thisWeekGroupLabel),
            headerId: FlagDueDateHeaderId.ThisWeek,
        };
    }
    if (due < weekAfterNextStart.valueOf()) {
        return {
            headerText: () => loc(nextWeekGroupLabel),
            headerId: FlagDueDateHeaderId.NextWeek,
        };
    }
    return {
        headerText: () => loc(laterGroupLabel),
        headerId: FlagDueDateHeaderId.Later,
    };
}
